import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { Tooltip } from 'syngenta-digital-cropwise-react-ui-kit';
import {
  AppliedAreaCell,
  ProductsCell,
  RecordTypeIconCell,
  ViewMoreCell
} from 'components/LinkedRecordCells';
import { DATE_TIME_FORMAT, LINKED_RECORD_NAME_LIST_CAP } from './constants';
import { getLinkedRecordDescriptor } from './typeDescriptors';
import { LinkedRecordRow } from './types';

const TOOLTIP_OVERLAY_STYLE = { backgroundColor: '#232630' };

const RecordNameCell = ({ record }: { record: LinkedRecordRow }) => {
  const [isTruncated, setIsTruncated] = useState(false);
  const nameRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const element = nameRef.current;
    if (!element) return undefined;

    const measure = () =>
      setIsTruncated(element.scrollWidth > element.clientWidth);

    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [record.name]);

  return (
    <div className="flex flex-col min-w-0">
      <Tooltip
        title={isTruncated ? record.name : ''}
        placement="top"
        overlayInnerStyle={TOOLTIP_OVERLAY_STYLE}
      >
        <span
          ref={nameRef}
          className="block truncate text-sm text-gray-900"
          data-testid={`record-name-${record.id}`}
        >
          {record.name}
        </span>
      </Tooltip>
      <span className="text-xs text-neutral-50">
        {record.startDate
          ? moment(record.startDate).format(DATE_TIME_FORMAT)
          : '---'}
      </span>
    </div>
  );
};

/**
 * Only the icon and View More cells are fixed; the rest share what's left. With
 * no `scroll.x` the table never scrolls sideways, so View More stays visible.
 */
const getLinkedRecordsColumns = (
  onViewMore: (record: LinkedRecordRow) => void
): Array<any> => [
  {
    title: '',
    dataIndex: 'recordType',
    key: 'recordType',
    width: 60,
    render: (_: any, record: LinkedRecordRow) => {
      const descriptor = getLinkedRecordDescriptor(record.type);
      const Icon = descriptor?.Icon;
      return (
        <RecordTypeIconCell
          icon={Icon ? <Icon width={20} height={20} /> : null}
          title={descriptor?.typeName ?? ''}
          testId={`record-type-icon-${record.id}`}
          hoverFill={descriptor?.iconHoverFill}
        />
      );
    }
  },
  {
    title: 'Record Name',
    dataIndex: 'name',
    key: 'name',
    ellipsis: true,
    render: (_: any, record: LinkedRecordRow) => (
      <RecordNameCell record={record} />
    )
  },
  {
    title: 'Applied Area',
    dataIndex: 'fieldsBrief',
    key: 'appliedArea',
    ellipsis: true,
    render: (_: any, record: LinkedRecordRow) => (
      <AppliedAreaCell record={record} maxItems={LINKED_RECORD_NAME_LIST_CAP} />
    )
  },
  {
    title: 'Products',
    dataIndex: 'productsBrief',
    key: 'products',
    ellipsis: true,
    render: (_: any, record: LinkedRecordRow) => (
      <ProductsCell record={record} maxItems={LINKED_RECORD_NAME_LIST_CAP} />
    )
  },
  {
    title: '',
    dataIndex: 'viewMore',
    key: 'viewMore',
    width: 133,
    render: (_: any, record: LinkedRecordRow) => (
      <ViewMoreCell
        onClick={() => onViewMore(record)}
        testId={`view-more-${record.id}`}
      />
    )
  }
];

export default getLinkedRecordsColumns;
